import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import * as maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import "./LocationManager.css";

const API = "https://api.komorotel.com/api";
const COMOROS_CENTER = [43.6, -11.75]; // [lng, lat]
const COMOROS_BBOX = "43.0,-12.6,44.6,-11.3";
const MIN_SAVE_ZOOM = 15;

/* ── Styles de carte ───────────────────────────── */

// MapLibre a besoin de son "worker" pour dessiner la carte. Le build ne le
// copie pas tout seul : il est servi depuis public/ (voir script prebuild).
maplibregl.setWorkerUrl("/maplibre-gl-worker.js");

// Plan détaillé : noms des villes, villages, quartiers, lieux (données OpenStreetMap)
const STYLE_PLAN = "https://tiles.openfreemap.org/styles/liberty";

// Secours : plan simple en images, si le plan détaillé ne charge pas
const STYLE_FALLBACK = {
  version: 8,
  sources: {
    plan: {
      type: "raster",
      tiles: ["https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}"],
      tileSize: 256,
      maxzoom: 18,
      attribution: "© Esri, © OpenStreetMap",
    },
  },
  layers: [{ id: "plan", type: "raster", source: "plan" }],
};

/* ── Utilitaires ───────────────────────────────── */

// Si le plan détaillé n'a rien affiché après quelques secondes (worker absent,
// service indisponible…), on bascule sur le plan simple pour ne jamais
// laisser une carte vide.
function attachFallback(map) {
  let rendered = false;
  let switched = false;
  const toFallback = () => {
    if (switched || rendered) return;
    switched = true;
    map.setStyle(STYLE_FALLBACK);
  };
  // "idle" = la carte a fini de s'afficher au moins une fois
  const onIdle = () => { rendered = true; clearTimeout(timer); };
  const onError = () => { if (!map.isStyleLoaded()) toFallback(); };
  const timer = setTimeout(toFallback, 10000);
  map.once("idle", onIdle);
  map.on("error", onError);
  return () => { clearTimeout(timer); map.off("idle", onIdle); map.off("error", onError); };
}

const hasCoords = (loc) => loc?.lat != null && loc?.lng != null;

function formatPlace(p) {
  if (!p) return "";
  const parts = [p.name, p.street, p.district || p.city || p.county, p.state];
  return [...new Set(parts.filter(Boolean))].join(", ");
}

/* ── Coordonnées collées ───────────────────────────
   Formats reconnus :
   - 11°38'18.3"S 43°15'53.1"E   (partage Google Maps)
   - -11.638417, 43.264750        (décimales)
   - 11.638417 S, 43.264750 E
   - liens Google Maps longs : .../@-11.63,43.26,17z  ou  ?q=-11.63,43.26
*/
function parseCoordinates(input) {
  const s = input
    .trim()
    .replace(/[’′]/g, "'")
    .replace(/[”″]|''/g, '"');

  const valid = (lat, lng) =>
    Number.isFinite(lat) && Number.isFinite(lng) &&
    Math.abs(lat) <= 90 && Math.abs(lng) <= 180 ? { lat, lng } : null;

  // Liens Google Maps longs
  // !3d…!4d… = position exacte du lieu ; @… = simple centre de l'écran
  const url =
    s.match(/!3d(-?\d+(?:\.\d+)?)!4d(-?\d+(?:\.\d+)?)/) ||
    s.match(/[?&](?:q|query|ll|destination)=(-?\d+(?:\.\d+)?),\s*(-?\d+(?:\.\d+)?)/) ||
    s.match(/@(-?\d+(?:\.\d+)?),\s*(-?\d+(?:\.\d+)?)/);
  if (url) return valid(parseFloat(url[1]), parseFloat(url[2]));

  // Degrés / minutes / secondes, ou degrés décimaux avec N/S/E/O
  const part =
    /(\d+(?:[.,]\d+)?)\s*°?\s*(?:(\d+(?:[.,]\d+)?)\s*'\s*)?(?:(\d+(?:[.,]\d+)?)\s*"\s*)?\s*([NSEWO])/gi;
  const parts = [...s.matchAll(part)];
  if (parts.length === 2) {
    const toDec = (m) => {
      const n = (v) => (v ? parseFloat(v.replace(",", ".")) : 0);
      let v = n(m[1]) + n(m[2]) / 60 + n(m[3]) / 3600;
      if (/[SWO]/i.test(m[4])) v = -v;
      return { v, axis: /[NS]/i.test(m[4]) ? "lat" : "lng" };
    };
    const a = toDec(parts[0]);
    const b = toDec(parts[1]);
    if (a.axis !== b.axis) {
      const lat = a.axis === "lat" ? a.v : b.v;
      const lng = a.axis === "lng" ? a.v : b.v;
      return valid(lat, lng);
    }
  }

  // Décimales simples : "-11.638417, 43.264750"
  const dec = s.match(/^(-?\d{1,2}\.\d+)\s*[,;\s]\s*(-?\d{1,3}\.\d+)$/);
  if (dec) return valid(parseFloat(dec[1]), parseFloat(dec[2]));

  return null;
}

const isShortMapsLink = (s) => /maps\.app\.goo\.gl|goo\.gl\/maps/i.test(s);

async function searchPlaces(q, signal) {
  const url = `https://photon.komoot.io/api/?q=${encodeURIComponent(q)}&limit=6&lang=fr&bbox=${COMOROS_BBOX}`;
  const res = await fetch(url, { signal });
  const json = await res.json();
  return (json.features || []).map((f) => ({
    lng: f.geometry.coordinates[0],
    lat: f.geometry.coordinates[1],
    title: f.properties.name || f.properties.street || "Lieu",
    subtitle: formatPlace({ ...f.properties, name: null }),
  }));
}

async function reverseGeocode(lat, lng, signal) {
  const res = await fetch(`https://photon.komoot.io/reverse?lat=${lat}&lon=${lng}&lang=fr`, { signal });
  const json = await res.json();
  return formatPlace(json.features?.[0]?.properties);
}

function targetRect() {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  if (vw <= 700) {
    return { top: vh * 0.05, left: 0, width: vw, height: vh * 0.95, radius: "24px 24px 0 0" };
  }
  const width = Math.min(1080, vw - 48);
  const height = Math.min(780, vh - 48);
  return { top: (vh - height) / 2, left: (vw - width) / 2, width, height, radius: "24px" };
}

const Pin = ({ size = 40 }) => (
  <svg width={size} height={size * 1.3} viewBox="0 0 34 44" xmlns="http://www.w3.org/2000/svg">
    <path d="M17 0C7.6 0 0 7.6 0 17c0 12.4 17 27 17 27s17-14.6 17-27C34 7.6 26.4 0 17 0z" fill="#174D3A" />
    <circle cx="17" cy="17" r="6.5" fill="#FFB32E" />
  </svg>
);

const Icon = {
  search: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" />
    </svg>
  ),
  locate: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <circle cx="12" cy="12" r="4" /><path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
    </svg>
  ),
  plus: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
  ),
  minus: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M5 12h14" /></svg>
  ),
  close: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
  ),
  pinOutline: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M12 22s7-6.1 7-12a7 7 0 10-14 0c0 5.9 7 12 7 12z" /><circle cx="12" cy="10" r="2.5" />
    </svg>
  ),
};

/* ── Fenêtre d'édition ─────────────────────────── */

function LocationModal({ origin, initial, city, onClose, onSave }) {
  const [phase, setPhase] = useState("from"); // from → open → closing
  const mapEl = useRef(null);
  const mapRef = useRef(null);

  const [center, setCenter] = useState(hasCoords(initial) ? initial : null);
  const [zoom, setZoom] = useState(hasCoords(initial) ? 17.5 : 8.6);
  const [moving, setMoving] = useState(false);
  const [address, setAddress] = useState("");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Animation d'ouverture
  useEffect(() => {
    const raf = requestAnimationFrame(() => requestAnimationFrame(() => setPhase("open")));
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && close();
    document.addEventListener("keydown", onKey);
    return () => {
      cancelAnimationFrame(raf);
      document.body.style.overflow = "";
      document.removeEventListener("keydown", onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Carte
  useEffect(() => {
    const start = hasCoords(initial);
    const map = new maplibregl.Map({
      container: mapEl.current,
      style: STYLE_PLAN,
      center: start ? [initial.lng, initial.lat] : COMOROS_CENTER,
      zoom: start ? 17.5 : 8.6,
      attributionControl: { compact: true },
      dragRotate: false,
      pitchWithRotate: false,
    });
    map.touchZoomRotate.disableRotation();
    mapRef.current = map;
    const stopFallback = attachFallback(map);

    const sync = () => {
      const c = map.getCenter();
      setCenter({ lat: c.lat, lng: c.lng });
      setZoom(map.getZoom());
    };

    map.on("movestart", () => setMoving(true));
    map.on("moveend", () => { setMoving(false); sync(); });
    map.on("click", (e) =>
      map.easeTo({ center: e.lngLat, zoom: Math.max(map.getZoom(), 16), duration: 600 })
    );
    map.on("load", async () => {
      sync();
      // Pas encore de point : on se place directement sur la ville de l'hôtel
      if (!start && city) {
        try {
          const [first] = await searchPlaces(city);
          if (first) map.flyTo({ center: [first.lng, first.lat], zoom: 14, duration: 1400 });
        } catch {}
      }
    });

    return () => { stopFallback(); map.remove(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Adresse sous le pin
  useEffect(() => {
    if (!center || zoom < 13) { setAddress(""); return; }
    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      try { setAddress(await reverseGeocode(center.lat, center.lng, ctrl.signal)); } catch {}
    }, 350);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [center, zoom]);

  // Recherche
  useEffect(() => {
    if (query.trim().length < 2) { setResults([]); return; }

    // Coordonnées collées (Google Maps, degrés ou décimales) : pas besoin de chercher
    const coords = parseCoordinates(query);
    if (coords) {
      setResults([{
        ...coords,
        exact: true,
        title: "Aller à ces coordonnées",
        subtitle: `${coords.lat.toFixed(6)}, ${coords.lng.toFixed(6)}`,
      }]);
      return;
    }

    if (isShortMapsLink(query)) {
      setResults([{
        info: true,
        title: "Lien court non pris en charge",
        subtitle: "Ouvrez le lien dans Google Maps et copiez les coordonnées affichées",
      }]);
      return;
    }

    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      try { setResults(await searchPlaces(query, ctrl.signal)); } catch {}
    }, 300);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [query]);

  const goTo = (r) => {
    if (r.info) return;
    // Coordonnées exactes : zoom maximal pour que le point tombe pile dessus
    mapRef.current?.flyTo({ center: [r.lng, r.lat], zoom: r.exact ? 18 : 17, duration: 1200 });
    if (!r.exact) setQuery(r.title);
    setSearchOpen(false);
  };

  const locateMe = () => {
    if (!navigator.geolocation) return setError("Géolocalisation indisponible sur cet appareil.");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        mapRef.current?.flyTo({ center: [pos.coords.longitude, pos.coords.latitude], zoom: 18, duration: 1200 });
        setError("");
      },
      () => setError("Localisation refusée. Autorisez-la dans le navigateur, ou utilisez la recherche.")
    );
  };

  const close = () => {
    setPhase("closing");
    setTimeout(onClose, 420);
  };

  const canSave = center && zoom >= MIN_SAVE_ZOOM && !moving;

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    setError("");
    try {
      await onSave(center);
      close();
    } catch {
      setError("Erreur lors de l'enregistrement.");
      setSaving(false);
    }
  };

  const from = origin
    ? { top: origin.top, left: origin.left, width: origin.width, height: origin.height, radius: "20px" }
    : targetRect();
  const rect = phase === "open" ? targetRect() : from;

  return createPortal(
    <div className={`lm-overlay ${phase === "open" ? "is-open" : ""}`} onMouseDown={close}>
      <div
        className={`lm-modal phase-${phase}`}
        style={{ top: rect.top, left: rect.left, width: rect.width, height: rect.height, borderRadius: rect.radius }}
        onMouseDown={(e) => e.stopPropagation()}
        onTransitionEnd={(e) => e.propertyName === "width" && mapRef.current?.resize()}
      >
        <div className="lm-content">
          {/* Carte */}
          <div className="lm-map" ref={mapEl} />

          {/* Pin fixe au centre */}
          <div className={`lm-center-pin ${moving ? "is-lifted" : ""}`}>
            <div className="lm-center-pin-icon"><Pin size={42} /></div>
            <div className="lm-center-pin-shadow" />
          </div>

          {/* Barre du haut */}
          <div className="lm-topbar">
            <div className={`lm-search ${searchOpen && results.length ? "has-results" : ""}`}>
              <span className="lm-search-icon">{Icon.search}</span>
              <input
                value={query}
                onChange={(e) => { setQuery(e.target.value); setSearchOpen(true); }}
                onFocus={() => setSearchOpen(true)}
                onKeyDown={(e) => e.key === "Enter" && results[0] && goTo(results[0])}
                placeholder="Rechercher un lieu, ou coller des coordonnées Google Maps"
              />
              {searchOpen && results.length > 0 && (
                <div className="lm-results">
                  {results.map((r, i) => (
                    <button key={i} className={`lm-result ${r.info ? "is-info" : ""}`} onClick={() => goTo(r)}>
                      <span className="lm-result-icon">{Icon.pinOutline}</span>
                      <span>
                        <span className="lm-result-title">{r.title}</span>
                        {r.subtitle && <span className="lm-result-sub">{r.subtitle}</span>}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button className="lm-round lm-close" onClick={close} aria-label="Fermer">{Icon.close}</button>
          </div>

          {/* Consigne */}
          {zoom < MIN_SAVE_ZOOM && (
            <div className="lm-toast">Zoomez sur l'établissement pour placer le point précisément</div>
          )}

          {/* Contrôles */}
          <div className="lm-controls">
            <button className="lm-round" onClick={locateMe} title="Je suis sur place">{Icon.locate}</button>
            <div className="lm-zoom">
              <button onClick={() => mapRef.current?.zoomIn()} aria-label="Zoom avant">{Icon.plus}</button>
              <button onClick={() => mapRef.current?.zoomOut()} aria-label="Zoom arrière">{Icon.minus}</button>
            </div>
          </div>

          {/* Panneau du bas */}
          <div className="lm-sheet">
            <div className="lm-sheet-info">
              <div className="lm-sheet-label">Emplacement de l'établissement</div>
              <div className="lm-sheet-address">
                {moving ? "…" : address || (zoom >= 13 ? "Adresse non trouvée" : "Déplacez la carte sous le repère")}
              </div>
              {center && zoom >= MIN_SAVE_ZOOM && (
                <div className="lm-sheet-coords">{center.lat.toFixed(6)}, {center.lng.toFixed(6)}</div>
              )}
              {error && <div className="lm-sheet-error">{error}</div>}
            </div>
            <div className="lm-sheet-actions">
              <button className="lm-btn ghost" onClick={close}>Annuler</button>
              <button className="lm-btn primary" onClick={save} disabled={!canSave || saving}>
                {saving ? "Enregistrement..." : "Confirmer l'emplacement"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ── Aperçu dans le bloc compact ───────────────── */

function PreviewMap({ loc }) {
  const el = useRef(null);
  useEffect(() => {
    const map = new maplibregl.Map({
      container: el.current,
      style: STYLE_PLAN,
      center: [loc.lng, loc.lat],
      zoom: 15.5,
      interactive: false,
      attributionControl: false,
    });
    const stopFallback = attachFallback(map);
    return () => { stopFallback(); map.remove(); };
  }, [loc.lat, loc.lng]);

  return (
    <div className="lm-preview-map" ref={el}>
      <div className="lm-preview-pin"><Pin size={26} /></div>
      <span className="lm-preview-credit">© OpenStreetMap</span>
    </div>
  );
}

/* ── Bloc compact sur le dashboard ─────────────── */

export default function LocationManager({ data, hotelId, refreshData }) {
  const cardRef = useRef(null);
  const needsRefresh = useRef(false);
  const [origin, setOrigin] = useState(null);
  const [open, setOpen] = useState(false);
  const [address, setAddress] = useState("");

  const location = data?.location;
  const placed = hasCoords(location);

  useEffect(() => {
    if (!placed) return;
    const ctrl = new AbortController();
    reverseGeocode(location.lat, location.lng, ctrl.signal).then(setAddress).catch(() => {});
    return () => ctrl.abort();
  }, [placed, location?.lat, location?.lng]);

  const openEditor = () => {
    setOrigin(cardRef.current.getBoundingClientRect());
    setOpen(true);
  };

  const handleSave = async ({ lat, lng }) => {
    await axios.patch(`${API}/hotels/${hotelId}/location`, { lat, lng }, { withCredentials: true });
    // On rafraîchit seulement une fois la fenêtre refermée,
    // pour ne pas couper l'animation de fermeture
    needsRefresh.current = true;
  };

  const handleClose = () => {
    setOpen(false);
    if (needsRefresh.current) {
      needsRefresh.current = false;
      refreshData?.();
    }
  };

  return (
    <>
      <div ref={cardRef} className={`lm-card ${open ? "is-hidden" : ""}`} onClick={openEditor}>
        <div className="lm-preview">
          {placed ? <PreviewMap loc={location} /> : <div className="lm-preview-empty">{Icon.pinOutline}</div>}
        </div>

        <div className="lm-card-body">
          <div className="lm-card-title">Emplacement</div>
          {placed ? (
            <>
              <div className="lm-card-address">{address || "Point placé sur la carte"}</div>
              <div className="lm-badge ok">Visible par les voyageurs</div>
            </>
          ) : (
            <>
              <div className="lm-card-address">Les voyageurs ne voient que la ville.</div>
              <div className="lm-badge warn">À compléter</div>
            </>
          )}
        </div>

        <button className="lm-btn primary small" onClick={(e) => { e.stopPropagation(); openEditor(); }}>
          {placed ? "Modifier" : "Placer le point"}
        </button>
      </div>

      {open && (
        <LocationModal
          origin={origin}
          initial={location}
          city={data?.city ? `${data.city}, Comores` : null}
          onClose={handleClose}
          onSave={handleSave}
        />
      )}
    </>
  );
}